import { Button, DialogContent, DialogTitle, Stack, TextField } from "@mui/material";
import { useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import { getFileSystemPath, isFileUrl, normalizeLinkUrl, tryGetLinkUrl } from "@entrilia/shared/utilities/urlHelper";
import { combineValidators, linkUrlValidator, requiredValidator } from "@entrilia/shared/utilities/validators";

interface Props {
  onClose: () => void;
  title: string;
  url: string;
  onSave: (title: string, url: string) => void;
}

interface FormState {
  title: string;
  url: string;
  touchedFields: ("title" | "url")[];
}

const validateUrlState = combineValidators(requiredValidator, linkUrlValidator);

const validateTitleState = requiredValidator;

const EditUrlDialog = ({ onClose, title, url, onSave }: Props) => {
  const [state, setState] = useState<FormState>({ title, url, touchedFields: [] });

  const handleSave = () => {
    onSave(state.title.trim(), normalizeLinkUrl(state.url));
    onClose();
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({
      ...prev,
      url: e.target.value,
      touchedFields: distinct([...prev.touchedFields, "url"]),
    }));
  };

  const handleUrlPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const urlText = e.clipboardData.getData("text");
    const url = tryGetLinkUrl(urlText);

    setState((prev) => ({
      ...prev,
      url: urlText,
      title: !prev.title && url ? (isFileUrl(url) ? getFileSystemPath(url) : url.href) : prev.title,
      touchedFields: distinct([...prev.touchedFields, "url", "title"]),
    }));
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({
      ...prev,
      title: e.target.value,
      touchedFields: distinct([...prev.touchedFields, "title"]),
    }));
  };

  const urlValidationResult = validateUrlState(state.url);
  const titleValidationResult = validateTitleState(state.title);

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Edit URL</DialogTitle>
      <DialogContent>
        <Stack spacing={2} useFlexGap sx={{ pt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="URL"
            value={state.url}
            onChange={handleUrlChange}
            onPaste={handleUrlPaste}
            error={!urlValidationResult.isValid && state.touchedFields.includes("url")}
            helperText={
              urlValidationResult.error && state.touchedFields.includes("url") ? urlValidationResult.error : undefined
            }
          />
          <TextField
            fullWidth
            label="Title"
            value={state.title}
            onChange={handleTitleChange}
            error={!titleValidationResult.isValid && state.touchedFields.includes("title")}
            helperText={
              titleValidationResult.error && state.touchedFields.includes("title")
                ? titleValidationResult.error
                : undefined
            }
          />
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!urlValidationResult.isValid || !titleValidationResult.isValid}
        >
          Save
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default EditUrlDialog;
